import { Control, Path, useFormContext } from 'react-hook-form';
import {
    IJobQuestionsForm,
    ISaveJobForm,
    JobQuestionObjectFields,
    QuestionType,
} from '../../../../../common';
import { useEffect, useRef } from 'react';

interface UseDealbreakerLogicProps {
    control: Control<IJobQuestionsForm>;
    index: number;
    fieldNamePrefix: `questions.${number}`;
    questionType?: string;
}

export const useDealbreakerLogic = ({
    control,
    index,
    fieldNamePrefix,
    questionType,
}: UseDealbreakerLogicProps) => {
    const { setValue, getValues } = useFormContext<ISaveJobForm>();
    const previousQuestionType = useRef(questionType);

    useEffect(() => {
        if (
            previousQuestionType.current &&
            previousQuestionType.current !== questionType &&
            (previousQuestionType.current === QuestionType.SINGLE_SELECT ||
                previousQuestionType.current ===
                    QuestionType.MULTIPLE_SELECT) &&
            (questionType === QuestionType.SINGLE_SELECT ||
                questionType === QuestionType.MULTIPLE_SELECT)
        ) {
            const options =
                getValues(
                    `${fieldNamePrefix}.${JobQuestionObjectFields.OPTIONS}` as Path<ISaveJobForm>
                ) || [];
            if (Array.isArray(options)) {
                options.forEach((_option, idx) => {
                    setValue(
                        `${fieldNamePrefix}.${JobQuestionObjectFields.OPTIONS}.${idx}.isCorrectAnswer` as Path<ISaveJobForm>,
                        false as never,
                        { shouldValidate: false }
                    );
                });
                setValue(
                    `${fieldNamePrefix}.${JobQuestionObjectFields.DEALBREAKER_ANSWER}` as Path<ISaveJobForm>,
                    null as never,
                    { shouldValidate: false }
                );
            }
        }
        previousQuestionType.current = questionType;
    }, [questionType, fieldNamePrefix, getValues, setValue]);

    const handleDealbreakerAnswerChange = (optionIndex: number) => {
        const optionsPath =
            `${fieldNamePrefix}.${JobQuestionObjectFields.OPTIONS}` as Path<ISaveJobForm>;
        const options =
            (getValues(optionsPath) as
                | Array<{ value: string; isCorrectAnswer?: boolean }>
                | undefined) || [];
        const isMultipleSelection =
            questionType === QuestionType.MULTIPLE_SELECT;

        if (isMultipleSelection) {
            const currentValue = options[optionIndex]?.isCorrectAnswer || false;
            setValue(
                `${fieldNamePrefix}.${JobQuestionObjectFields.OPTIONS}.${optionIndex}.isCorrectAnswer` as Path<ISaveJobForm>,
                !currentValue as never,
                { shouldValidate: true, shouldDirty: true }
            );

            const updatedOptions = [...options];
            updatedOptions[optionIndex] = {
                ...updatedOptions[optionIndex],
                isCorrectAnswer: !currentValue,
            };

            const selectedOptions = updatedOptions
                .filter((opt) => opt.isCorrectAnswer)
                .map((opt) => opt.value);

            setValue(
                `${fieldNamePrefix}.${JobQuestionObjectFields.DEALBREAKER_ANSWER}` as Path<ISaveJobForm>,
                (selectedOptions.length > 0
                    ? selectedOptions.join(',')
                    : null) as never,
                { shouldValidate: true, shouldDirty: true }
            );
        } else {
            options.forEach((_option, idx) => {
                setValue(
                    `${fieldNamePrefix}.${JobQuestionObjectFields.OPTIONS}.${idx}.isCorrectAnswer` as Path<ISaveJobForm>,
                    (idx === optionIndex) as never,
                    { shouldValidate: true, shouldDirty: true }
                );
            });

            const selectedOptionValue = options[optionIndex]?.value;
            setValue(
                `${fieldNamePrefix}.${JobQuestionObjectFields.DEALBREAKER_ANSWER}` as Path<ISaveJobForm>,
                (selectedOptionValue || null) as never,
                { shouldValidate: true, shouldDirty: true }
            );
        }
    };

    const handleBooleanDealbreakerChange = (answer: 'true' | 'false') => {
        setValue(
            `${fieldNamePrefix}.${JobQuestionObjectFields.DEALBREAKER_ANSWER}` as Path<ISaveJobForm>,
            answer as never,
            { shouldValidate: true, shouldDirty: true }
        );
    };

    return {
        handleDealbreakerAnswerChange,
        handleBooleanDealbreakerChange,
    };
};
